import { createFileRoute } from "@tanstack/react-router";
import { PageLayout } from "../../components/PageLayout";

export const Route = createFileRoute("/_layout/terms")({
    head: () => ({
        meta: [
            { name: "title", content: "Terms of Service - StoryDoodle" },
            {
                name: "description",
                content:
                    "Terms of Service for StoryDoodle - Interactive AI-powered storytelling platform for families and children.",
            },
            { property: "og:title", content: "Terms of Service - StoryDoodle" },
            {
                property: "og:description",
                content:
                    "Terms of Service for StoryDoodle - Interactive AI-powered storytelling platform for families and children.",
            },
            { property: "og:url", content: "https://storydoodle.app/terms" },
        ],
    }),
    component: Terms,
});

function Terms() {
    return (
        <PageLayout title="Terms of Service" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Terms" }]}>
            <main className="container mx-auto px-4 sm:px-6 py-8 max-w-4xl">
                <div className="space-y-8">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
                        <p className="text-muted-foreground">Last updated: July 8, 2025</p>
                    </div>

                    <div className="prose prose-lg max-w-none space-y-6">
                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Geographic Restrictions</h2>
                            <p className="text-foreground leading-relaxed">
                                This service is only available to residents of the United States. By using StoryDoodle,
                                you represent and warrant that you are a US resident and are not accessing this service
                                from within the European Union or any other restricted jurisdiction.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Acceptance of Terms</h2>
                            <p className="text-foreground leading-relaxed">
                                By creating an account and using StoryDoodle, you agree to these Terms of Service. If
                                you are under 18, your parent or guardian must agree to these terms on your behalf.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Age Requirements</h2>
                            <p className="text-foreground leading-relaxed">
                                You must be at least 13 years old to create a StoryDoodle account. Children under 13 may
                                use StoryDoodle with a parent or guardian who creates and manages the account.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Account Responsibilities</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">When using StoryDoodle, you agree to:</p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>Provide accurate information when creating your account</li>
                                    <li>Keep your account secure and not share login credentials</li>
                                    <li>Use the service in a respectful and appropriate manner</li>
                                    <li>Not create harmful, inappropriate, or illegal content</li>
                                </ul>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Content Guidelines</h2>
                            <p className="text-foreground leading-relaxed">
                                StoryDoodle is designed for creative, family-friendly storytelling. Content that is
                                inappropriate, harmful, or violates these guidelines may be removed and could result in
                                account suspension.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Your Content</h2>
                            <p className="text-foreground leading-relaxed">
                                You retain ownership of the drawings and stories you create. By using StoryDoodle, you
                                grant us permission to store, process, and display your content as necessary to provide
                                our service, including using AI to analyze your drawings and generate story content.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Subscription Plans & Pricing</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    StoryDoodle offers both free and paid subscription plans:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>
                                        <strong>Free Plan:</strong> Limited daily usage with basic features
                                    </li>
                                    <li>
                                        <strong>Plus Plan:</strong> $7/month or $70/year - 50 daily stories, premium AI,
                                        story customization, print & export
                                    </li>
                                    {/* <li>
                                        <strong>Pro Plan:</strong> $12/month or $120/year - Highest usage limits, all
                                        features
                                    </li>
                                    <li>
                                        <strong>Lifetime Plans:</strong> One-time purchase options available for Plus
                                        and Pro
                                    </li> */}
                                </ul>
                                <p className="text-foreground leading-relaxed">
                                    All prices are in USD and subject to applicable taxes. Subscription plans
                                    automatically renew unless cancelled before the next billing cycle.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">
                                Payment Processing & Merchant of Record
                            </h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    Payments are processed by Polar.sh, which acts as the merchant of record for all
                                    transactions. Polar.sh uses Stripe as the underlying payment processor. By
                                    subscribing to a paid plan, you agree to:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-foreground ml-4">
                                    <li>Provide accurate and complete payment information</li>
                                    <li>Authorize recurring charges for subscription plans</li>
                                    <li>Keep payment methods current and valid</li>
                                    <li>Pay all charges incurred on your account</li>
                                    <li>Comply with the Stripe Connected Account Agreement</li>
                                </ul>
                                <p className="text-foreground leading-relaxed">
                                    Payment processing is subject to Polar's terms of service, Stripe's payment terms,
                                    and applicable Connected Account Agreement. Polar.sh may collect service fees from
                                    transactions as disclosed at checkout.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Billing & Automatic Renewal</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    Subscription plans automatically renew at the end of each billing period unless
                                    cancelled before the renewal date. You will be charged the current subscription rate
                                    at the time of renewal.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    <strong>Failed Payment Handling:</strong> If payment fails, we will attempt to
                                    collect payment using your stored payment method. If payment continues to fail, your
                                    account may be suspended after reasonable notice. You remain responsible for all
                                    charges until your subscription is properly cancelled. To restore access after
                                    failed payment, you must update your payment information and resolve any outstanding
                                    charges.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">
                                Billing Management & Cancellation
                            </h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    <strong>Billing Management:</strong> You can manage your subscription, update
                                    payment methods, and access billing history through your account settings or by
                                    using the "Manage Subscription" button which redirects to Polar.sh's secure billing
                                    portal.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    <strong>Cancellation:</strong> You may cancel your subscription at any time through
                                    your account settings or billing portal. Cancellation must be completed before your
                                    next billing cycle to avoid charges. Cancellation will take effect at the end of
                                    your current billing period.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    <strong>Refund Policy:</strong> Subscription fees are generally non-refundable.
                                    Service access continues until the end of your current billing period after
                                    cancellation. Lifetime purchases are non-refundable. Cancellation refunds are not
                                    prorated - you will not receive refunds for unused portions of your billing period.
                                    However, plan upgrades and downgrades may be subject to prorated billing adjustments
                                    as handled by Polar.sh.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    For billing disputes, technical issues, or exceptional circumstances, please contact
                                    our support team. We may provide refunds at our discretion for technical failures or
                                    other qualifying issues.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Feature Access & Usage Limits</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    Your subscription plan determines your access to features and daily usage limits.
                                    These limits reset daily and are designed to ensure fair usage across all users.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    We reserve the right to modify features, usage limits, or pricing with reasonable
                                    notice to existing subscribers. Changes will take effect at your next billing cycle.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Taxes & Compliance</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    You are responsible for any applicable taxes on your subscription fees. Polar.sh
                                    acts as the merchant of record for tax compliance purposes and may collect tax
                                    information as required by law.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    <strong>Tax Information Requirements:</strong> You may be required to provide tax
                                    identification information, including but not limited to tax ID numbers, for
                                    compliance with applicable tax laws. You agree to provide accurate tax information
                                    when requested and to cooperate with tax reporting requirements.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    Sales tax, VAT, or other applicable taxes may be added to your subscription fees
                                    based on your billing address and local tax requirements.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Service Availability</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    We strive to keep StoryDoodle available and running smoothly, but we cannot
                                    guarantee uninterrupted service. We may need to perform maintenance or updates that
                                    temporarily affect availability.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    Additionally, our AI-powered story generation features depend on third-party
                                    artificial intelligence services. Service interruptions, rate limiting, or outages
                                    of these external services may temporarily affect AI story generation and related
                                    features, even when other parts of StoryDoodle remain operational.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Account Termination</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    You may delete your account data at any time from your account settings. We may
                                    suspend or terminate accounts that violate these terms, abuse fair usage policies,
                                    are inactive for extended periods, or have failed payment obligations.
                                </p>
                                <p className="text-foreground leading-relaxed">
                                    If your account is suspended due to payment issues, you may regain access by
                                    updating your payment information and resolving any outstanding charges. Account
                                    termination will cancel any active subscriptions.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Limitation of Liability</h2>
                            <div className="space-y-3">
                                <p className="text-foreground leading-relaxed">
                                    StoryDoodle is provided "as is" for creative and educational purposes. We are not
                                    liable for any damages arising from use of the service.
                                </p>

                                <p className="text-foreground leading-relaxed">
                                    <strong>User Content Responsibility:</strong> You are solely responsible for all
                                    content you input into StoryDoodle, including drawings, prompts, and any other
                                    user-generated content. You must use the service lawfully and appropriately. Parents
                                    and guardians are responsible for supervising children's use of the service.
                                </p>

                                <p className="text-foreground leading-relaxed">
                                    <strong>AI-Generated Content:</strong> AI-generated stories and content are created
                                    by artificial intelligence and may be unpredictable despite our safety measures. We
                                    do not guarantee the appropriateness, accuracy, or quality of AI-generated content.
                                    Users should review all AI-generated content before sharing or using it outside the
                                    service.
                                </p>

                                <p className="text-foreground leading-relaxed">
                                    <strong>Content Moderation:</strong> While we implement safety measures and content
                                    guidelines, we cannot guarantee that all content will be appropriate or error-free.
                                    Our safety measures are best-effort and not foolproof. Users should report any
                                    problematic content they encounter.
                                </p>

                                <p className="text-foreground leading-relaxed">
                                    We provide no warranties of any kind and are not liable for any user-generated
                                    content, AI-generated content, or any damages, losses, or harm arising from the use
                                    of StoryDoodle.
                                </p>
                            </div>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Changes to Terms</h2>
                            <p className="text-foreground leading-relaxed">
                                We may update these terms from time to time. Users will be notified of significant
                                changes by email or through the app.
                            </p>
                        </section>

                        <section className="space-y-4">
                            <h2 className="text-2xl font-semibold text-foreground">Contact Information</h2>
                            <p className="text-foreground leading-relaxed">
                                Questions about these terms? Visit our{" "}
                                <a href="/help" className="text-primary hover:underline font-medium">
                                    help page
                                </a>{" "}
                                or email us at{" "}
                                <a
                                    href="mailto:help@storydoodle.app"
                                    className="text-primary hover:underline font-medium"
                                >
                                    help@storydoodle.app
                                </a>
                                .
                            </p>
                        </section>
                    </div>
                </div>
            </main>
        </PageLayout>
    );
}
